import type { GetContractUsageBreakdownResponse } from '@vy/api-definition';
import { GetContractUsageBreakdownResponseSchema } from '@vy/api-definition';
import type { UsageByDateBreakdown } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import { useMemo } from 'react';
import type { QueryClient } from 'react-query';
import { useQuery } from 'react-query';
import type { SelectItem } from '../../../../../../../../components/new-design/molecules';
import { getPrivateApi } from '../../../../../../../../services/api';
import { queryKeys } from '../../../../../../../../services/query-key/query-keys';

type UseContractUsageBreakdownResult = {
  isLoading: boolean;
  usageByDateBreakdowns: UsageByDateBreakdown[];
  usageProducts: SelectItem[];
};

async function contractUsageBreakdownQuery(contractId: string): Promise<GetContractUsageBreakdownResponse> {
  const api = getPrivateApi();
  const response = await api.getContractUsageBreakdown({ contractId });

  return GetContractUsageBreakdownResponseSchema.parse(response);
}

export function useContractUsageBreakdown(contractId: string): UseContractUsageBreakdownResult {
  const { isLoading, data } = useQuery({
    queryKey: queryKeys.contractUsageBreakdown(contractId),
    queryFn: () => contractUsageBreakdownQuery(contractId),
  });

  const usageByDateBreakdowns = useMemo(() => {
    if (!isNotNullOrUndefined(data)) {
      return [];
    }

    return mapToChartData(data);
  }, [data]);

  const usageProducts = useMemo<SelectItem[]>(() => {
    if (!isNotNullOrUndefined(data)) {
      return [];
    }

    const seen = new Set<string>();
    const items: SelectItem[] = [];

    for (const breakdown of data.usageByDateBreakdown) {
      for (const { productId, productName } of breakdown.productUsage) {
        if (seen.has(productId)) {
          continue;
        }

        seen.add(productId);
        items.push({ value: productId, label: productName });
      }
    }

    return items;
  }, [data]);

  return { isLoading, usageByDateBreakdowns, usageProducts };
}

export async function prefetchContractUsageBreakdown(contractId: string, queryClient: QueryClient) {
  await queryClient.prefetchQuery(queryKeys.contractUsageBreakdown(contractId), () =>
    contractUsageBreakdownQuery(contractId),
  );
}

function mapToChartData(response: GetContractUsageBreakdownResponse): UsageByDateBreakdown[] {
  return response.usageByDateBreakdown.map((entry) => ({
    date: entry.date,
    units: entry.units,
    customerUsage: entry.productUsage.map((product) => ({
      customerId: product.productId,
      customerName: product.productName,
      units: product.units,
    })),
  }));
}
